import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'unistore/react'
import i18n from 'i18next'
import scrollToElement from 'scroll-to-element'
import * as actions from '../helpers/actions.jsx'
import {UploadInput} from '../Uploader/index.jsx'
import {getLinkToConfidentiality} from '../helpers/externalRoutes.jsx'
import {
  getContactFormSubjects,
  getContactFormPreselectedSubject,
  hideHtmlStaticContactForm,
  simpleModal,
  scrollToFirstInvalidInput,
} from '../Services/domBridge.jsx'
import {sessionGetCurrentTimezone} from '../Services/storage.jsx'
import {
  isEmailValid,
  getBrowserTimeZone,
  apiErrorToMessage,
} from '../Services/helpers.jsx'
import {sendMessage} from '../Services/api.jsx'
import * as gtm from '../Services/gtm.jsx'
import Spinner from './Spinner.jsx'
import Input from './Input.jsx'

class ContactFormInterpreting extends React.Component {
  static propTypes = {
    // store props
    files: PropTypes.array.isRequired,
    // store actions
    removeFiles: PropTypes.func.isRequired,
  }

  state = {
    email: '',
    name: '',
    subject: getContactFormPreselectedSubject() || (getContactFormSubjects()[0] || {id: 'quote', name: 'Quote'}),
    subjects: getContactFormSubjects().length > 0 ? getContactFormSubjects() : [{id: 'quote', name: 'Quote'}],
    message: '',
    acceptedConditions: false,
    isFormSubmitted: false,
    trackSent: false,
    isPending: false,
    errors: {
      email: {
        error: false,
        message: i18n.t('Components.ContactForm.emailInvalidMessage'),
      },
    },
  }

  componentDidMount = () => {
    this.props.removeFiles(true) //remove contact form files
    hideHtmlStaticContactForm()

    // Add language change listener
    i18n.on('languageChanged', () => {
      this.forceUpdate()
    })
  }

  componentWillUnmount = () => {
    // Remove language change listener
    i18n.off('languageChanged')
  }

  updateTextField = (key) => (value) => {
    this.setState({[key]: value})
  }

  updateAcceptedConditions = () => {
    this.setState({acceptedConditions: !this.state.acceptedConditions})
  }

  onSubmit = async (event) => {
    event.preventDefault()

    let {files, removeFiles} = this.props
    const {
      subject: previousSubject,
      email,
      acceptedConditions,
      name,
      message,
    } = this.state

    files = files.filter((file) => !file.uploadFailed && file.fromContactForm)
    const fileUrls = files.map((file) => file.url)

    const emailIsValid = isEmailValid(email)
    const isMessageValid = message.length > 0

    this.validateEmail(email)
    this.setState({
      isFormSubmitted: true,
      isPending: true,
    })

    if (acceptedConditions && emailIsValid && isMessageValid) {
      const currency = {id: 'EUR', name: 'Euro (EUR)'}

      const sessionTimezone = sessionGetCurrentTimezone()
      const browserTimezone = getBrowserTimeZone()

      const timeZone = sessionTimezone || browserTimezone
      const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone

      try {
        const result = await sendMessage(
          name,
          email,
          previousSubject,
          message,
          fileUrls,
          currency,
          timeZone,
          timezoneName,
        )

        simpleModal(
          i18n.t('Components.ContactForm.messageSentFeedback'),
          i18n.t('Components.ContactForm.messageSentDescription'),
          'check',
        )
        const subjects = getContactFormSubjects()
        const subject = getContactFormPreselectedSubject() || (subjects[0] || {id: 'quote', name: 'Quote'})
        this.setState({
          isPending: false,
          isFormSubmitted: false,
          trackSent: true,
          email: '',
          name: '',
          subject,
          subjects: subjects.length > 0 ? subjects : [{id: 'quote', name: 'Quote'}],
          acceptedConditions: false,
          files: [],
          message: '',
        })

        // clean files list
        removeFiles(true)

        gtm.trackSentForm()
      } catch (error) {
        console.error('ContactFormInterpreting sendMessage error:', error)
        simpleModal('Error', apiErrorToMessage(error), 'times', true)
        this.setState({isPending: false})
      }
    } else {
      if (!emailIsValid) {
        scrollToElement('#contactForm', {
          offset: -64,
          ease: 'in-out-sine',
          duration: 800,
        })
      } else {
        setTimeout(scrollToFirstInvalidInput, 250)
      }
      this.setState({isPending: false})
    }
  }

  validateEmail = (value) => {
    let {errors} = this.state
    errors.email.error = !isEmailValid(value)
    this.setState({errors})
  }

  render = () => {
    const {
      trackSent,
      email,
      errors,
      name,
      message,
      acceptedConditions,
      isPending,
      isFormSubmitted,
    } = this.state
    const showValidations = isFormSubmitted
    const isMessageValid = message.length > 0

    return (
      <div className={'contact-form'}>
        <form
          onSubmit={this.onSubmit}
          id={trackSent ? 'track-sent' : undefined}
          data-form-sent={trackSent ? 1 : 0}
        >
          <Input
            id="contact-form-email"
            className={'contact-form__input'}
            label={i18n.t('Components.ContactForm.emailLabel')}
            placeholder={i18n.t('Components.ContactForm.emailPlaceholder')}
            isMandatory={true}
            invalidMessage={errors.email.message}
            onChange={this.updateTextField('email')}
            onBlurInput={this.validateEmail}
            value={email}
            isValid={!errors.email.error}
            showValidation={true}
            maxLength={200}
          />
          <Input
            id="contact-form-name"
            className={'contact-form__input'}
            label={i18n.t('Components.ContactForm.nameLabel')}
            placeholder={i18n.t('Components.ContactForm.namePlaceholder')}
            value={name}
            onChange={this.updateTextField('name')}
            isValid={true}
            showValidation={showValidations}
            maxLength={45}
          />
          <UploadInput
            className={'contact-form__input'}
            label={i18n.t('Components.ContactForm.fileLabel')}
            placeholder={i18n.t('Components.ContactForm.filePlaceholder')}
            chooseFilesLabel={i18n.t(
              'Components.WordCountOrUpload.uploadFileButton',
            )}
            onChange={Function.prototype}
            uploaderProps={{
              headerLabel: i18n.t('Components.ContactForm.fileLabel'),
              submitLabel: i18n.t(
                'Components.UploaderModal.submitButtonForUpload',
              ),
              contactForm: true,
            }}
            wordCountEnabled={false}
          />
          <Input
            id="contact-form-message"
            className={'contact-form__input'}
            label={i18n.t('Components.ContactForm.messageLabelInterpreting')}
            placeholder={i18n.t('Components.ContactForm.messagePlaceholder')}
            onChange={this.updateTextField('message')}
            value={message}
            isMandatory={true}
            isValid={isMessageValid}
            showValidation={showValidations}
            type={'textarea'}
            rows={4}
            maxLength={16777215}
          />
          <div
            className={`contact-form__conditions ${
              showValidations && !acceptedConditions
                ? 'contact-form__conditions--invalid'
                : ''
            }`}
          >
            <input
              type={'checkbox'}
              id={'contactFormAcceptedConditions'}
              checked={acceptedConditions}
              onChange={this.updateAcceptedConditions}
            />
            <label
              htmlFor={'contactFormAcceptedConditions'}
              className={`${
                showValidations && !acceptedConditions ? 'label--invalid' : ''
              }`}
            >
              {i18n.t('Components.ContactForm.conditionsStatic')}
              <a href={getLinkToConfidentiality()} className={'link'} target="_blank" rel="noopener noreferrer">
                {i18n.t('Components.ContactForm.conditionsPrivacy')}
              </a>
            </label>
            {showValidations && !acceptedConditions && (
              <span className={'contact-form__conditions__invalid-message'}>
                {i18n.t('Components.ContactForm.conditionsInvalidMessage')}
              </span>
            )}
          </div>

          {isPending ? (
            <div className={'button'}>
              <Spinner isWhite={true} />
            </div>
          ) : (
            <button type={'submit'}>
              {i18n.t('Components.ContactForm.submitButton')}
            </button>
          )}
        </form>
      </div>
    )
  }
}

export default connect(['files'], actions)(ContactFormInterpreting)
